import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
    AlertTriangle,
    Check,
    ChevronDown,
    Copy,
    FileText,
    Pencil,
    Plus,
    Search,
    Sparkles,
    Trash2,
    Upload,
    X,
} from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { EmptyState } from "@/components/ui/empty";
import { Badge } from "@/components/ui/badge";
import { createPage, deletePage, listPagesPaged, updatePage } from "@/api/pages";
import { Pagination } from "@/components/pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RowActions } from "@/components/ui/row-actions";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { apiErrorMessage } from "@/lib/api";
import { cn, formatRelativeTime } from "@/lib/utils";
import type { PageDto } from "@/types/api";
import { AdvertiserSelect } from "@/components/advertiser-select";
import { AdvertiserCell, useAdvertiserLookup } from "@/components/advertiser-cell";
import { ownerFilterFromUrl, ownerFilterParam, ownerFilterToUrl } from "@/api/commerce";

export function PagesListPage(): React.ReactNode {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const [open, setOpen] = React.useState(false);
    const [newMenuOpen, setNewMenuOpen] = React.useState(false);
    const [name, setName] = React.useState("");
    const [description, setDescription] = React.useState("");

    const PAGE_SIZE = 50;
    const [pageIndex, setPageIndex] = React.useState(0);
    const [searchInput, setSearchInput] = React.useState("");
    const [search, setSearch] = React.useState("");
    React.useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim()), 250);
        return () => clearTimeout(t);
    }, [searchInput]);
    React.useEffect(() => setPageIndex(0), [search]);

    const [sort, setSort] = React.useState<"createdAt,desc" | "createdAt,asc" | "name,asc" | "name,desc">("createdAt,desc");

    // Advertiser filter — URL-persisted via ?advertiserId= so a filtered
    // view survives reload + can be shared. `undefined` means "All",
    // `null` means "Unassigned" (wired to backend sentinel -1).
    const [searchParams, setSearchParams] = useSearchParams();
    const advertiserId = ownerFilterFromUrl(searchParams.get("advertiserId"));
    const setAdvertiserId = React.useCallback(
        (next: number | null | undefined): void => {
            setSearchParams(
                (p) => {
                    const v = ownerFilterToUrl(next);
                    if (v == null) p.delete("advertiserId");
                    else p.set("advertiserId", v);
                    return p;
                },
                { replace: true },
            );
            setPageIndex(0);
        },
        [setSearchParams],
    );

    // One global advertiser fetch — also gates whether the Advertiser
    // column is shown at all (hidden when none exist).
    const advLookup = useAdvertiserLookup();
    const showAdvertiserColumn = advLookup.advertisers.length > 0;

    const pagesQuery = useQuery({
        queryKey: ["pages", pageIndex, search, sort, advertiserId],
        queryFn: () => listPagesPaged({
            page: pageIndex,
            size: PAGE_SIZE,
            sort,
            q: search || undefined,
            advertiserId: ownerFilterParam(advertiserId),
        }),
        placeholderData: (prev) => prev,
    });

    const rows: PageDto[] = pagesQuery.data?.content ?? [];

    // Group by name to surface duplicates: count + ids per name across the
    // current page's rows.
    const dupes = React.useMemo(() => {
        const m = new Map<string, number[]>();
        for (const p of rows) {
            const k = (p.name || "").trim().toLowerCase();
            if (!m.has(k)) m.set(k, []);
            m.get(k)!.push(p.id);
        }
        return m;
    }, [rows]);

    const [selected, setSelected] = React.useState<Set<number>>(new Set());
    React.useEffect(() => setSelected(new Set()), [pageIndex, search, sort]);
    const allSelected = rows.length > 0 && rows.every((p) => selected.has(p.id));
    const toggleAll = () => {
        const next = new Set<number>();
        if (!allSelected) for (const p of rows) next.add(p.id);
        setSelected(next);
    };
    const toggle = (id: number) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelected(next);
    };

    const [editingId, setEditingId] = React.useState<number | null>(null);
    const [editValue, setEditValue] = React.useState("");

    const renameMut = useMutation({
        mutationFn: ({ id, newName }: { id: number; newName: string }) => updatePage(id, { name: newName }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["pages"] });
            toast.success("Renamed");
            setEditingId(null);
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Rename failed")),
    });

    const deleteMut = useMutation({
        mutationFn: (id: number) => deletePage(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["pages"] });
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Delete failed")),
    });

    const bulkDelete = () => {
        const ids = [...selected];
        if (ids.length === 0) return;
        confirm.show({
            title: `Delete ${ids.length} page${ids.length === 1 ? "" : "s"}?`,
            description: "Pages with active domains attached will fail and stay in the list. This can't be undone.",
            destructive: true,
            confirmLabel: "Delete",
            "data-testid": "bulk-delete-confirm",
            onConfirm: async () => {
                const results = await Promise.allSettled(ids.map((id) => deletePage(id)));
                const ok = results.filter((r) => r.status === "fulfilled").length;
                const failed = results.length - ok;
                queryClient.invalidateQueries({ queryKey: ["pages"] });
                setSelected(new Set());
                if (failed === 0) toast.success(`Deleted ${ok} page${ok === 1 ? "" : "s"}`);
                else toast.error(`Deleted ${ok} of ${results.length} — ${failed} failed (probably had domains attached)`);
            },
        });
    };

    const deleteOlderDupes = () => {
        const ids: number[] = [];
        for (const [, group] of dupes) {
            if (group.length < 2) continue;
            // Rows are already sorted by the server; the first id in the
            // group is the newest if sort=createdAt,desc — drop the rest.
            const sortedRows = group
                .map((id) => rows.find((r) => r.id === id)!)
                .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
            for (let i = 1; i < sortedRows.length; i++) ids.push(sortedRows[i].id);
        }
        if (ids.length === 0) {
            toast.info("No duplicates to delete on this page.");
            return;
        }
        confirm.show({
            title: `Delete ${ids.length} older duplicate${ids.length === 1 ? "" : "s"}?`,
            description: "Keeps the newest copy of each duplicate name. This can't be undone.",
            destructive: true,
            confirmLabel: "Delete duplicates",
            onConfirm: async () => {
                const results = await Promise.allSettled(ids.map((id) => deletePage(id)));
                const ok = results.filter((r) => r.status === "fulfilled").length;
                queryClient.invalidateQueries({ queryKey: ["pages"] });
                toast.success(`Deleted ${ok} older duplicate${ok === 1 ? "" : "s"}`);
            },
        });
    };

    const onRowDelete = (p: PageDto) => {
        confirm.show({
            title: `Delete "${p.name}"?`,
            description: "Removes the page record. Versions and any captured leads stay. This can't be undone.",
            destructive: true,
            confirmLabel: "Delete page",
            "data-testid": `delete-confirm-${p.id}`,
            onConfirm: async () => {
                await new Promise<void>((resolve, reject) => {
                    deleteMut.mutate(p.id, {
                        onSuccess: () => { toast.success(`Deleted "${p.name}"`); resolve(); },
                        onError: (e) => reject(e),
                    });
                });
            },
        });
    };

    const createMut = useMutation({
        mutationFn: () => createPage({ name, description: description || null }),
        onSuccess: (page) => {
            queryClient.invalidateQueries({ queryKey: ["pages"] });
            toast.success(`Page "${page.name}" created`);
            setOpen(false);
            setName("");
            setDescription("");
            navigate(`/pages/${page.id}`);
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Failed to create page")),
    });

    const dupCount = [...dupes.values()].filter((g) => g.length > 1).reduce((a, g) => a + (g.length - 1), 0);

    return (
        <>
            <PageHeader
                title="Pages"
                description="Logical landing-page campaigns. Each page can have many uploaded bundle versions."
                actions={
                    <>
                    {/* Split entry point: clicking the icon shows three paths so the
                        user picks the right one upfront instead of going to a "New
                        page" dialog only to realise they wanted AI / Upload. */}
                    <div className="relative">
                        <Button data-testid="new-page-button" onClick={() => setNewMenuOpen((o) => !o)}>
                            <Plus className="h-4 w-4" />
                            New page
                            <ChevronDown className="h-3.5 w-3.5" />
                        </Button>
                        {newMenuOpen && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={() => setNewMenuOpen(false)} />
                                <div className="absolute right-0 top-full z-50 mt-1 w-64 rounded-md border border-border bg-popover p-1 shadow-md" data-testid="new-page-menu">
                                    <button
                                        type="button"
                                        onClick={() => { setNewMenuOpen(false); setOpen(true); }}
                                        className="flex w-full items-start gap-2 rounded px-2 py-2 text-left text-sm hover:bg-accent"
                                        data-testid="new-page-empty"
                                    >
                                        <FileText className="mt-0.5 h-4 w-4 text-muted-foreground" />
                                        <span>
                                            <div className="font-medium">Empty page</div>
                                            <div className="text-xs text-muted-foreground">Create a record, upload a bundle next.</div>
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setNewMenuOpen(false); navigate("/pages/new?mode=ai"); }}
                                        className="flex w-full items-start gap-2 rounded px-2 py-2 text-left text-sm hover:bg-accent"
                                        data-testid="new-page-ai"
                                    >
                                        <Sparkles className="mt-0.5 h-4 w-4 text-primary" />
                                        <span>
                                            <div className="font-medium">Start with AI</div>
                                            <div className="text-xs text-muted-foreground">Describe what you want, or fork an existing URL.</div>
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setNewMenuOpen(false); navigate("/pages/new?mode=ai-upload"); }}
                                        className="flex w-full items-start gap-2 rounded px-2 py-2 text-left text-sm hover:bg-accent"
                                        data-testid="new-page-upload"
                                    >
                                        <Upload className="mt-0.5 h-4 w-4 text-muted-foreground" />
                                        <span>
                                            <div className="font-medium">Upload a bundle</div>
                                            <div className="text-xs text-muted-foreground">Drop a .zip; the AI Constructor opens with it.</div>
                                        </span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>New page</DialogTitle>
                                <DialogDescription>
                                    Create a new logical landing page. You'll upload your first bundle on the next screen.
                                </DialogDescription>
                            </DialogHeader>
                            <form
                                className="space-y-4"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    createMut.mutate();
                                }}
                            >
                                <div className="space-y-2">
                                    <Label htmlFor="page-name">Name</Label>
                                    <Input
                                        id="page-name"
                                        data-testid="page-name-input"
                                        placeholder="weightloss-offer"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        autoFocus
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="page-description">Description (optional)</Label>
                                    <Textarea
                                        id="page-description"
                                        data-testid="page-description-input"
                                        placeholder="Internal description — not shown to visitors."
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        rows={3}
                                    />
                                </div>
                                <DialogFooter>
                                    <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={createMut.isPending}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" data-testid="create-page-submit" disabled={!name.trim() || createMut.isPending}>
                                        {createMut.isPending ? <Spinner size="sm" /> : "Create page"}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                    </>
                }
            />
            <div className="space-y-3 px-6 py-4">
                {/* Toolbar */}
                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex flex-1 items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 min-w-[260px]">
                        <Search className="h-4 w-4 text-muted-foreground" />
                        <Input
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search by name (case-insensitive)…"
                            className="h-7 border-0 bg-transparent px-0 focus-visible:ring-0"
                            data-testid="pages-search"
                        />
                    </div>
                    <Select value={sort} onValueChange={(v) => setSort(v as typeof sort)}>
                        <SelectTrigger className="h-9 w-[170px]" data-testid="pages-sort" aria-label="Sort pages">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="createdAt,desc">Newest first</SelectItem>
                            <SelectItem value="createdAt,asc">Oldest first</SelectItem>
                            <SelectItem value="name,asc">Name (A→Z)</SelectItem>
                            <SelectItem value="name,desc">Name (Z→A)</SelectItem>
                        </SelectContent>
                    </Select>
                    {showAdvertiserColumn && (
                        <div className="w-[200px]">
                            <AdvertiserSelect
                                value={advertiserId}
                                onChange={setAdvertiserId}
                                data-testid="pages-advertiser-filter"
                            />
                        </div>
                    )}
                    {selected.size > 0 ? (
                        <Button variant="destructive" size="sm" onClick={bulkDelete} data-testid="bulk-delete">
                            <Trash2 className="h-4 w-4" />
                            Delete {selected.size}
                        </Button>
                    ) : dupCount > 0 ? (
                        <Button variant="outline" size="sm" onClick={deleteOlderDupes} data-testid="dedupe-button">
                            <Copy className="h-4 w-4" />
                            Delete {dupCount} older duplicate{dupCount === 1 ? "" : "s"}
                        </Button>
                    ) : null}
                </div>

                {/* Table */}
                {pagesQuery.isLoading ? (
                    <div className="flex items-center justify-center py-16"><Spinner size="lg" /></div>
                ) : rows.length === 0 ? (
                    <EmptyState
                        icon={<FileText className="h-8 w-8" />}
                        title={search ? `No pages match "${search}"` : "No pages yet"}
                        description={search ? "Try a different search term." : "Create your first landing-page campaign."}
                        action={
                            !search ? (
                                <Button onClick={() => setOpen(true)}>
                                    <Plus className="h-4 w-4" /> New page
                                </Button>
                            ) : undefined
                        }
                    />
                ) : (
                    <div className="overflow-hidden rounded-md border border-border">
                        <table className="w-full text-sm" data-testid="pages-table">
                            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="w-10 px-3 py-2 text-left">
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            onChange={toggleAll}
                                            aria-label="Select all"
                                            data-testid="select-all"
                                        />
                                    </th>
                                    <th className="px-3 py-2 text-left">Name</th>
                                    <th className="px-3 py-2 text-left">Description</th>
                                    {showAdvertiserColumn && (
                                        <th className="px-3 py-2 text-left">Advertiser</th>
                                    )}
                                    <th className="px-3 py-2 text-left">Updated</th>
                                    <th className="w-24 px-3 py-2 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {rows.map((p) => {
                                    const dupGroup = dupes.get((p.name || "").trim().toLowerCase());
                                    const isDupe = dupGroup && dupGroup.length > 1;
                                    const isEditing = editingId === p.id;
                                    return (
                                        <tr
                                            key={p.id}
                                            className={cn(
                                                "group hover:bg-muted/30",
                                                selected.has(p.id) && "bg-primary/5",
                                            )}
                                            data-testid={`page-row-${p.id}`}
                                        >
                                            <td className="px-3 py-2">
                                                <input
                                                    type="checkbox"
                                                    checked={selected.has(p.id)}
                                                    onChange={() => toggle(p.id)}
                                                    aria-label={`Select ${p.name}`}
                                                    data-testid={`select-${p.id}`}
                                                />
                                            </td>
                                            <td className="px-3 py-2">
                                                {isEditing ? (
                                                    <form
                                                        className="flex items-center gap-1.5"
                                                        onSubmit={(e) => {
                                                            e.preventDefault();
                                                            const v = editValue.trim();
                                                            if (v && v !== p.name) renameMut.mutate({ id: p.id, newName: v });
                                                            else setEditingId(null);
                                                        }}
                                                    >
                                                        <Input
                                                            autoFocus
                                                            className="h-7"
                                                            value={editValue}
                                                            onChange={(e) => setEditValue(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Escape") setEditingId(null);
                                                            }}
                                                            data-testid={`rename-input-${p.id}`}
                                                        />
                                                        <Button type="submit" size="sm" variant="ghost" disabled={renameMut.isPending} data-testid={`rename-save-${p.id}`}>
                                                            <Check className="h-3.5 w-3.5" />
                                                        </Button>
                                                        <Button type="button" size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                                                            <X className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </form>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => navigate(`/pages/${p.id}`)}
                                                            className="text-left font-medium hover:underline"
                                                        >
                                                            {p.name}
                                                        </button>
                                                        {isDupe && (
                                                            <Badge variant="outline" className="border-amber-400/50 bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
                                                                <AlertTriangle className="mr-1 h-3 w-3" />
                                                                {dupGroup!.length} same
                                                            </Badge>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                void navigator.clipboard.writeText(String(p.id));
                                                                toast.success(`Copied page id #${p.id}`);
                                                            }}
                                                            className="rounded text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground"
                                                            title="Copy id"
                                                        >
                                                            #{p.id}
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-muted-foreground">{p.description || "—"}</td>
                                            {showAdvertiserColumn && (
                                                <td className="px-3 py-2">
                                                    <AdvertiserCell advertiserId={p.advertiserId} lookup={advLookup.byId} />
                                                </td>
                                            )}
                                            <td className="px-3 py-2 text-muted-foreground">{formatRelativeTime(p.updatedAt)}</td>
                                            <td className="px-3 py-2 text-right">
                                                <RowActions>
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="ghost"
                                                        title="Edit in Constructor"
                                                        aria-label={`Edit ${p.name} in Constructor`}
                                                        onClick={() => navigate(`/pages/${p.id}/edit`)}
                                                        data-testid={`edit-in-constructor-${p.id}`}
                                                    >
                                                        <Sparkles className="h-3.5 w-3.5" />
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="ghost"
                                                        title="Rename"
                                                        aria-label={`Rename ${p.name}`}
                                                        onClick={() => {
                                                            setEditingId(p.id);
                                                            setEditValue(p.name);
                                                        }}
                                                        data-testid={`rename-${p.id}`}
                                                    >
                                                        <Pencil className="h-3.5 w-3.5" />
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="ghost"
                                                        title="Delete"
                                                        aria-label={`Delete ${p.name}`}
                                                        onClick={() => onRowDelete(p)}
                                                        data-testid={`delete-${p.id}`}
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                                                    </Button>
                                                </RowActions>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                {pagesQuery.data && rows.length > 0 && (
                    <Pagination
                        page={pagesQuery.data}
                        pageSize={PAGE_SIZE}
                        currentPage={pageIndex}
                        onPage={setPageIndex}
                    />
                )}
            </div>
            {confirm.dialog}
        </>
    );
}
